import { useKBar } from 'kbar'
import type { ReactNode } from 'react'
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { toast } from 'react-hot-toast'
import { trpc } from '@/lib/trpc'
import { useCoachingQuestions } from '../hooks/use-questions'
import { useSeries } from '../hooks/use-series'
import type { CoachingQuestion } from '../types'
import type {
  CommandPaletteExternalAPI,
  CommandPaletteMode,
  FollowUpData,
  FollowUpInput,
  FollowUpPhase,
  MergePhase,
  PendingAction,
  ProblemToRemove,
  SeriesData,
} from './types'

// Internal context for state management (used by action modules)
interface CommandPaletteState {
  // Mode
  mode: CommandPaletteMode

  // Series state
  selectedSeries: SeriesData | null
  seriesProblems: CoachingQuestion[]
  lockedSeriesId: string | null
  problemsToRemove: ProblemToRemove[]
  allSeries: SeriesData[]
  problemIdsInSeries: Set<string>

  // Merge state
  mergePhase: MergePhase
  mergePrimaryProblem: CoachingQuestion | null
  mergeDuplicateProblem: CoachingQuestion | null

  // Follow-up state
  followUpPhase: FollowUpPhase
  followUpTargetProblem: CoachingQuestion | null
  followUpExistingFollowUps: FollowUpData[]
  followUpInput: FollowUpInput

  // Pending action for programmatic control
  pendingAction: PendingAction | null

  // Actions
  startMode: (mode: CommandPaletteMode) => void
  cancelMode: () => void
  addProblem: (problem: CoachingQuestion) => void
  selectSeries: (series: SeriesData) => void
  selectProblemToRemove: (
    problemId: string,
    seriesId: string,
    problemTitle: string,
  ) => void
  saveSeries: () => Promise<void>
  removeFromSeries: () => Promise<void>

  // Merge actions
  selectMergePrimary: (problem: CoachingQuestion) => void
  selectMergeDuplicate: (problem: CoachingQuestion) => void
  confirmMerge: () => Promise<void>
  cancelMerge: () => void

  // Follow-up actions
  selectFollowUpProblem: (problem: CoachingQuestion) => Promise<void>
  setFollowUpPrompt: (prompt: string) => void
  setFollowUpTimeComplexity: (timeComplexity: string | null) => void
  saveFollowUp: (spaceComplexity?: string | null) => Promise<void>
  cancelFollowUp: () => void

  // Palette control
  clearPendingAction: () => void

  // Data refresh
  refreshSeries: () => Promise<unknown>
  refreshQuestions: () => Promise<unknown>
}

const CommandPaletteStateContext = createContext<CommandPaletteState | null>(
  null,
)

// External API context (for components outside the palette)
const CommandPaletteAPIContext =
  createContext<CommandPaletteExternalAPI | null>(null)

export function useCommandPaletteState() {
  const context = use(CommandPaletteStateContext)
  if (!context) {
    throw new Error(
      'useCommandPaletteState must be used within CommandPaletteProvider',
    )
  }
  return context
}

export function useCommandPalette() {
  const context = use(CommandPaletteAPIContext)
  if (!context) {
    throw new Error(
      'useCommandPalette must be used within CommandPaletteProvider',
    )
  }
  return context
}

interface CommandPaletteProviderProps {
  children: ReactNode
}

export function CommandPaletteProvider({
  children,
}: CommandPaletteProviderProps) {
  const { refresh: refreshQuestions } = useCoachingQuestions()
  const {
    series: allSeries,
    problemIdsInSeries,
    refresh: refreshSeries,
  } = useSeries()

  const utils = trpc.useUtils()

  // tRPC mutations
  const createSeriesMutation = trpc.admin.series.create.useMutation()
  const addProblemsMutation = trpc.admin.series.addProblems.useMutation()
  const removeProblemsMutation = trpc.admin.series.removeProblems.useMutation()
  const linkDuplicateMutation = trpc.admin.questions.linkDuplicate.useMutation()
  const createFollowUpMutation = trpc.admin.followUps.create.useMutation()

  // Mode state
  const [mode, setMode] = useState<CommandPaletteMode>('idle')

  // Series state
  const [selectedSeries, setSelectedSeries] = useState<SeriesData | null>(null)
  const [seriesProblems, setSeriesProblems] = useState<CoachingQuestion[]>([])
  const [lockedSeriesId, setLockedSeriesId] = useState<string | null>(null)
  const [problemsToRemove, setProblemsToRemove] = useState<ProblemToRemove[]>(
    [],
  )

  // Merge state
  const [mergePhase, setMergePhase] = useState<MergePhase>('select-primary')
  const [mergePrimaryProblem, setMergePrimaryProblem] =
    useState<CoachingQuestion | null>(null)
  const [mergeDuplicateProblem, setMergeDuplicateProblem] =
    useState<CoachingQuestion | null>(null)

  // Follow-up state
  const [followUpPhase, setFollowUpPhase] =
    useState<FollowUpPhase>('select-problem')
  const [followUpTargetProblem, setFollowUpTargetProblem] =
    useState<CoachingQuestion | null>(null)
  const [followUpExistingFollowUps, setFollowUpExistingFollowUps] = useState<
    FollowUpData[]
  >([])
  const [followUpInput, setFollowUpInput] = useState<FollowUpInput>({
    prompt: '',
    expectedTimeComplexity: null,
    expectedSpaceComplexity: null,
  })
  // Bumped by every follow-up target selection and by reset, so a lookup that
  // was still in flight when either happened can tell it has been superseded.
  const followUpSelectionRef = useRef(0)

  // Pending action for programmatic control
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)

  // Reset all state
  const resetState = useCallback(() => {
    setMode('idle')
    setSelectedSeries(null)
    setSeriesProblems([])
    setLockedSeriesId(null)
    setProblemsToRemove([])
    setMergePhase('select-primary')
    setMergePrimaryProblem(null)
    setMergeDuplicateProblem(null)
    setFollowUpPhase('select-problem')
    setFollowUpTargetProblem(null)
    setFollowUpExistingFollowUps([])
    setFollowUpInput({
      prompt: '',
      expectedTimeComplexity: null,
      expectedSpaceComplexity: null,
    })
    followUpSelectionRef.current++
  }, [])

  // Start a specific mode
  const startMode = useCallback(
    (newMode: CommandPaletteMode) => {
      resetState()
      setMode(newMode)
    },
    [resetState],
  )

  // Cancel current mode
  const cancelMode = useCallback(() => {
    resetState()
  }, [resetState])

  // Add a problem to the series being created/edited
  const addProblem = useCallback((problem: CoachingQuestion) => {
    setSeriesProblems((prev) => [...prev, problem])
  }, [])

  // Select a series to add problems to
  const selectSeries = useCallback((series: SeriesData) => {
    setSelectedSeries(series)
    setMode('add-to-series')
  }, [])

  // Select a problem to remove from series
  const selectProblemToRemove = useCallback(
    (problemId: string, seriesId: string, problemTitle: string) => {
      const series = allSeries.find((s) => s.id === seriesId)
      if (!series) return

      // Lock to this series if not already locked
      if (!lockedSeriesId) {
        setLockedSeriesId(seriesId)
      }

      setProblemsToRemove((prev) => [
        ...prev,
        {
          problemId,
          seriesId,
          problemTitle,
          seriesName: series.name,
        },
      ])
    },
    [allSeries, lockedSeriesId],
  )

  // Save series (create or add to)
  const saveSeries = useCallback(async () => {
    try {
      const problemIds = seriesProblems.map((p) => p.id)

      if (mode === 'create-series') {
        if (seriesProblems.length < 2) {
          toast.error('At least 2 problems are required')
          return
        }

        const seriesName = seriesProblems[0].name
          .split(' ')
          .slice(0, 4)
          .join(' ')

        await createSeriesMutation.mutateAsync({
          name: seriesName,
          problemIds,
        })

        toast.success(`Series created with ${seriesProblems.length} problems!`)
      } else if (mode === 'add-to-series' && selectedSeries) {
        if (seriesProblems.length < 1) {
          toast.error('Select at least 1 problem to add')
          return
        }

        await addProblemsMutation.mutateAsync({
          id: selectedSeries.id,
          problemIds,
        })

        toast.success(
          `Added ${seriesProblems.length} problem${seriesProblems.length > 1 ? 's' : ''} to ${selectedSeries.name}!`,
        )
      }

      resetState()
      await refreshSeries()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save')
    }
  }, [
    mode,
    seriesProblems,
    selectedSeries,
    createSeriesMutation,
    addProblemsMutation,
    refreshSeries,
    resetState,
  ])

  // Remove problems from series
  const removeFromSeries = useCallback(async () => {
    if (!lockedSeriesId || problemsToRemove.length === 0) {
      toast.error('No problems selected for removal')
      return
    }

    try {
      const problemIds = problemsToRemove.map((p) => p.problemId)
      const seriesName = problemsToRemove[0].seriesName

      const result = await removeProblemsMutation.mutateAsync({
        id: lockedSeriesId,
        problemIds,
      })

      if (result.seriesDeleted) {
        toast.success(
          `Removed ${problemsToRemove.length} problem${problemsToRemove.length > 1 ? 's' : ''} and deleted series "${seriesName}"`,
        )
      } else {
        toast.success(
          `Removed ${problemsToRemove.length} problem${problemsToRemove.length > 1 ? 's' : ''} from ${seriesName}`,
        )
      }

      resetState()
      await refreshSeries()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove')
    }
  }, [
    lockedSeriesId,
    problemsToRemove,
    removeProblemsMutation,
    refreshSeries,
    resetState,
  ])

  // Merge: cancel
  const cancelMerge = useCallback(() => {
    resetState()
  }, [resetState])

  // Merge: select primary problem
  const selectMergePrimary = useCallback((problem: CoachingQuestion) => {
    setMergePrimaryProblem(problem)
    setMergePhase('select-duplicate')
  }, [])

  // Link duplicate: select second problem and show preview
  const selectMergeDuplicate = useCallback((problem: CoachingQuestion) => {
    setMergeDuplicateProblem(problem)
    setMergePhase('preview')
  }, [])

  // Link duplicate: confirm
  const confirmMerge = useCallback(async () => {
    if (!mergePrimaryProblem || !mergeDuplicateProblem) return

    try {
      await linkDuplicateMutation.mutateAsync({
        canonicalProblemId: mergePrimaryProblem.id,
        duplicateProblemId: mergeDuplicateProblem.id,
      })

      toast.success('Linked as duplicate')

      resetState()
      await Promise.all([refreshSeries(), refreshQuestions()])
    } catch (error: unknown) {
      const trpcError = error as { message?: string }
      toast.error(
        trpcError.message ??
          (error instanceof Error ? error.message : 'Failed to link problems'),
      )
    }
  }, [
    mergePrimaryProblem,
    mergeDuplicateProblem,
    linkDuplicateMutation,
    refreshSeries,
    refreshQuestions,
    resetState,
  ])

  // Follow-up: select target problem and fetch existing follow-ups
  const selectFollowUpProblem = useCallback(
    async (problem: CoachingQuestion) => {
      const selection = ++followUpSelectionRef.current
      setFollowUpTargetProblem(problem)

      let existingFollowUps: FollowUpData[] = []
      try {
        const data = await utils.admin.questions.getFollowUps.fetch({
          problemId: problem.id,
        })
        existingFollowUps = (data.followUps || []).map((f) => ({
          id: f.id,
          prompt: f.prompt,
          order: f.order,
          expectedTimeComplexity: f.expectedTimeComplexity,
          expectedSpaceComplexity: f.expectedSpaceComplexity,
          prereqs: f.prereqs,
        }))
      } catch {
        // Silently fail - we can still add follow-ups without knowing existing ones
      }

      // The results list stays open during the lookup, so the admin may have
      // picked a different problem (or cancelled) before this response landed.
      // Its follow-ups would become the new follow-up's default prereqs under
      // the wrong problem, so a superseded lookup must not touch state.
      if (followUpSelectionRef.current !== selection) return

      setFollowUpExistingFollowUps(existingFollowUps)
      setFollowUpPhase('enter-prompt')
    },
    [utils],
  )

  // Follow-up: set the prompt and advance to time complexity
  const setFollowUpPrompt = useCallback((prompt: string) => {
    setFollowUpInput((prev) => ({ ...prev, prompt: prompt.trim() }))
    setFollowUpPhase('enter-time-complexity')
  }, [])

  // Follow-up: set the time complexity and advance to space complexity
  const setFollowUpTimeComplexity = useCallback(
    (timeComplexity: string | null) => {
      setFollowUpInput((prev) => ({
        ...prev,
        expectedTimeComplexity: timeComplexity?.trim() || null,
      }))
      setFollowUpPhase('enter-space-complexity')
    },
    [],
  )

  // Follow-up: save the complete follow-up
  const saveFollowUp = useCallback(
    async (spaceComplexity?: string | null) => {
      if (!followUpTargetProblem) {
        toast.error('No problem selected')
        return
      }

      if (!followUpInput.prompt) {
        toast.error('Follow-up prompt is required')
        return
      }

      try {
        // Calculate default prereqs from existing follow-ups (leaf nodes)
        // A leaf node is a follow-up that is NOT a prerequisite of any other follow-up
        const prereqIds = new Set(
          followUpExistingFollowUps.flatMap((f) => f.prereqs),
        )
        const leafIds = followUpExistingFollowUps
          .filter((f) => f.id && !prereqIds.has(f.id))
          .map((f) => f.id)

        await createFollowUpMutation.mutateAsync({
          problemId: followUpTargetProblem.id,
          prompt: followUpInput.prompt,
          expectedTimeComplexity: followUpInput.expectedTimeComplexity,
          expectedSpaceComplexity: spaceComplexity?.trim() || null,
          prereqs: leafIds, // Smart default: require all leaf nodes
          approachIds: [], // No approaches in quick-add
        })

        toast.success(
          `Follow-up added to "${followUpTargetProblem.name.substring(0, 30)}..."`,
        )
        resetState()
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Failed to save follow-up',
        )
      }
    },
    [
      followUpTargetProblem,
      followUpInput,
      followUpExistingFollowUps,
      createFollowUpMutation,
      resetState,
    ],
  )

  // Follow-up: cancel
  const cancelFollowUp = useCallback(() => {
    resetState()
  }, [resetState])

  // Clear pending action
  const clearPendingAction = useCallback(() => {
    setPendingAction(null)
  }, [])

  // Handle keyboard shortcut for saving/removing/merging (Cmd+Enter)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        if (mode === 'merge' && mergePhase === 'preview') {
          e.preventDefault()
          confirmMerge()
          return
        }

        if (mode === 'remove-from-series' && problemsToRemove.length >= 1) {
          e.preventDefault()
          removeFromSeries()
          return
        }

        // Follow-up: save when in space-complexity phase
        if (
          mode === 'add-follow-up' &&
          followUpPhase === 'enter-space-complexity' &&
          followUpInput.prompt
        ) {
          e.preventDefault()
          // Get current search input value for space complexity
          const input = document.getElementById(
            'kbar-search-input',
          ) as HTMLInputElement | null
          const spaceComplexity = input?.value.trim() || null
          saveFollowUp(spaceComplexity)
          return
        }

        const minToSave = mode === 'create-series' ? 2 : 1
        const canSave =
          (mode === 'create-series' || mode === 'add-to-series') &&
          seriesProblems.length >= minToSave

        if (canSave) {
          e.preventDefault()
          saveSeries()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    mode,
    mergePhase,
    followUpPhase,
    followUpInput.prompt,
    seriesProblems.length,
    problemsToRemove.length,
    saveSeries,
    removeFromSeries,
    confirmMerge,
    saveFollowUp,
  ])

  // External API for programmatic control
  const handleOpenCreateSeries = useCallback(
    (initialProblem: CoachingQuestion) => {
      setMode('create-series')
      setSeriesProblems([initialProblem])
      setPendingAction({ type: 'create', problem: initialProblem })
    },
    [],
  )

  const handleOpenAddToSeries = useCallback((series: SeriesData) => {
    setMode('add-to-series')
    setSelectedSeries(series)
    setSeriesProblems([])
    setPendingAction({ type: 'add', series })
  }, [])

  const handleOpenMergeProblems = useCallback(() => {
    startMode('merge')
    setPendingAction({ type: 'merge' })
  }, [startMode])

  const stateValue: CommandPaletteState = {
    mode,
    selectedSeries,
    seriesProblems,
    lockedSeriesId,
    problemsToRemove,
    allSeries,
    problemIdsInSeries,
    mergePhase,
    mergePrimaryProblem,
    mergeDuplicateProblem,
    followUpPhase,
    followUpTargetProblem,
    followUpExistingFollowUps,
    followUpInput,
    pendingAction,
    startMode,
    cancelMode,
    addProblem,
    selectSeries,
    selectProblemToRemove,
    saveSeries,
    removeFromSeries,
    selectMergePrimary,
    selectMergeDuplicate,
    confirmMerge,
    cancelMerge,
    selectFollowUpProblem,
    setFollowUpPrompt,
    setFollowUpTimeComplexity,
    saveFollowUp,
    cancelFollowUp,
    clearPendingAction,
    refreshSeries,
    refreshQuestions,
  }

  const apiValue: CommandPaletteExternalAPI = {
    openCreateSeries: handleOpenCreateSeries,
    openAddToSeries: handleOpenAddToSeries,
    openMergeProblems: handleOpenMergeProblems,
  }

  return (
    <CommandPaletteStateContext value={stateValue}>
      <CommandPaletteAPIContext value={apiValue}>
        {children}
      </CommandPaletteAPIContext>
    </CommandPaletteStateContext>
  )
}

// Controller component that uses useKBar to toggle palette when a pending action is set
export function PaletteController() {
  const { pendingAction, clearPendingAction } = useCommandPaletteState()
  const { query } = useKBar()

  useEffect(() => {
    if (pendingAction) {
      query.toggle()
      clearPendingAction()
    }
  }, [pendingAction, query, clearPendingAction])

  return null
}
